// A REGRA do Leitor Inteligente de Processos que roda no navegador — pura, e
// com prova própria (`app/tests/processos.mjs`).
//
// Duas coisas moram aqui, e as duas vieram do `src/lib/pdf-utils.ts` da origem:
// a DETECÇÃO do tipo de perícia por palavras-chave, e a conta do que cabe no
// teto de texto. Nenhuma das duas toca em rede, banco ou DOM.
//
// A extração em si (que precisa do pdf.js e do navegador) está em
// `lib/pdfTexto.ts`. A separação existe porque a regra se prova em Node, num
// comando de meio segundo, e a extração só se prova em Chrome.

export type TipoPericia = "contabil" | "trabalhista" | "financeira-bancaria";

/**
 * O teto de texto por análise, o mesmo do servidor
 * (`_shared/leitor-de-processos.ts`, onde está a medição inteira que o escolheu).
 *
 * **1.000.000 desde 03/09/2026**, contra os 120.000 que o porte herdou da
 * Lovable sem nunca medir. O que decide não é o modelo — a janela dele aceita
 * ~2 milhões de caracteres e os 120.000 usavam 6% dela — e sim os **150 s de
 * wall clock da Edge Function**: 1.000.000 leva 83 s no pior caso de três
 * rodadas, e 1.400.000 leva 118,6 s (reprodutível), que não deixa folga.
 *
 * São cerca de **565 páginas** a 1.771 caracteres por página, a média medida no
 * processo real de 98 páginas.
 *
 * Os dois números TÊM de continuar iguais: esta tela avisa ANTES do envio e o
 * servidor grava DEPOIS, e o caso 10 da prova pura compara as duas contas.
 */
export const TETO_DE_TEXTO = 1000000;

/** Quantos arquivos uma análise aceita — o mesmo teto de `lp_criar_analise`. */
export const MAXIMO_DE_ARQUIVOS = 10;

export const NOME_DO_TIPO: Record<TipoPericia, string> = {
  contabil: "Contábil",
  trabalhista: "Trabalhista",
  "financeira-bancaria": "Financeira / Bancária",
};

/**
 * As palavras-chave, verbatim de `src/lib/pdf-utils.ts` da origem: 16 termos
 * para trabalhista, 13 para contábil e 17 para financeira/bancária.
 *
 * Cada termo aparece nas duas grafias — com e sem acento — porque o texto sai do
 * pdf.js e vem como o PDF o gravou; PDF antigo digitalizado costuma perder o
 * acento. A lista NÃO foi reescrita nem "melhorada": Victor pediu a ferramenta
 * como ela é, e mexer aqui mudaria em silêncio o tipo detectado de um processo.
 */
const PALAVRAS: Record<TipoPericia, string[]> = {
  trabalhista: [
    "reclamação trabalhista", "reclamacao trabalhista", "clt", "ctps",
    "horas extras", "rescisão", "rescisao", "fgts", "vara do trabalho",
    "reclamante", "reclamada", "adicional noturno", "insalubridade",
    "periculosidade", "dissídio", "dissidio",
  ],
  contabil: [
    "balanço", "balanco", "demonstrações contábeis", "demonstracoes contabeis",
    "cpc", "auditoria", "lucro", "patrimônio líquido", "patrimonio liquido",
    "contabilidade", "escrituração", "escrituracao", "razão contábil",
  ],
  "financeira-bancaria": [
    "contrato bancário", "contrato bancario", "juros", "taxa de juros",
    "financiamento", "empréstimo", "emprestimo", "banco", "cdc",
    "anatocismo", "capitalização", "capitalizacao", "spread bancário",
    "tabela price", "sac", "amortização", "amortizacao",
  ],
};

/** Quantos termos cada tipo tem — o número entra na prova, para a lista não
 *  encurtar em silêncio. */
export const QUANTOS_TERMOS: Record<TipoPericia, number> = {
  trabalhista: PALAVRAS.trabalhista.length,
  contabil: PALAVRAS.contabil.length,
  "financeira-bancaria": PALAVRAS["financeira-bancaria"].length,
};

/** A amostra que a detecção lê. Verbatim da origem: os 30.000 primeiros. */
export const AMOSTRA_DA_DETECCAO = 30000;

export type Deteccao = {
  tipo: TipoPericia | null;
  /** Quantas ocorrências cada tipo somou — é o que a tela mostra ao explicar. */
  pontos: Record<TipoPericia, number>;
  /** Verdadeiro quando dois tipos empataram no topo com pontuação > 0. */
  empate: boolean;
};

/**
 * Detecta o tipo de perícia pelas palavras-chave.
 *
 * Devolve os PONTOS junto com a escolha, e não só o tipo, por uma razão que a
 * origem não tinha: lá a tela dizia "✨ Detectado automaticamente" sem dizer com
 * base em quê. Um palpite sem procedência é lido como certeza — a mesma lição do
 * teto do MEI, onde a explicação passou a sair de onde o número sai.
 *
 * E o EMPATE é devolvido em vez de resolvido por ordem de lista. Na origem quem
 * decide o empate é a posição do tipo dentro do objeto — ou seja, um detalhe de
 * implementação escolhendo o tipo de perícia. É a cicatriz do inferidor do
 * Calendário, onde a ordem das candidatas escolheu a regra que não é a lei.
 */
export function detectarTipo(texto: string): Deteccao {
  const amostra = texto.slice(0, AMOSTRA_DA_DETECCAO).toLowerCase();

  const pontos: Record<TipoPericia, number> = {
    trabalhista: 0,
    contabil: 0,
    "financeira-bancaria": 0,
  };

  for (const tipo of Object.keys(PALAVRAS) as TipoPericia[]) {
    for (const termo of PALAVRAS[tipo]) {
      // `split` conta as ocorrências sem regex: o termo vem de uma lista fixa,
      // mas montar regex a partir de texto é onde nasce a próxima cicatriz de
      // escape (e a origem monta, com `new RegExp(kw, "gi")`).
      pontos[tipo] += amostra.split(termo).length - 1;
    }
  }

  const ordenado = (Object.keys(pontos) as TipoPericia[])
    .map((t) => ({ tipo: t, n: pontos[t] }))
    // Desempate ESTÁVEL por nome, e não pela ordem do objeto: ordem de chave é
    // detalhe de implementação, e aqui ela decidiria o tipo da perícia.
    .sort((a, b) => (b.n - a.n) || a.tipo.localeCompare(b.tipo));

  const melhor = ordenado[0];
  const segundo = ordenado[1];

  if (melhor.n === 0) return { tipo: null, pontos, empate: false };
  if (segundo && segundo.n === melhor.n) {
    // Empatou: a tela pede para o aluno escolher, em vez de fingir que sabe.
    return { tipo: null, pontos, empate: true };
  }
  return { tipo: melhor.tipo, pontos, empate: false };
}

// ===========================================================================
// A conta do teto — o que a tela DIZ ao perito antes de ele gastar uma análise
// ===========================================================================

export type Cota = {
  /** Quantos caracteres o modelo vai ler. */
  enviados: number;
  /** Quantos ficam de fora. Zero quando tudo cabe. */
  deFora: number;
  /** A cota por arquivo, quando há mais de um. */
  cotaPorArquivo: number | null;
  /** Cabe inteiro? */
  cabe: boolean;
  /** Quantas PÁGINAS o modelo vai ler — contadas, não estimadas. */
  paginasLidas: number;
  /** Quantas páginas o processo tem. */
  paginasTotais: number;
};

/**
 * Quantas páginas há num texto extraído — CONTANDO os marcadores `[Página N]`.
 *
 * A mesma função de `supabase/functions/_shared/leitor-de-processos.ts`, e as
 * duas têm de continuar dando o mesmo número: a tela avisa ANTES do envio, o
 * servidor grava DEPOIS. O caso 12 da prova pura compara as duas contas.
 */
export function contarPaginas(texto: string): number {
  return (texto.match(/\[Página \d+\]/g) ?? []).length;
}

/**
 * Quanto do processo vai ser lido, dado o tamanho e o número de arquivos.
 *
 * Repete o critério de `cortar()` do servidor de propósito: aqui é para AVISAR
 * antes, ali é para gravar depois. As duas contas têm de dar o mesmo número, e
 * o caso 10 da prova confere isso comparando com o valor que o servidor grava.
 *
 * A origem não tem esta função porque não tem este aviso: ela corta em 120.000
 * e não conta a ninguém, então o perito recebe um relatório com aparência de
 * completo sobre o primeiro terço de um processo grande.
 */
export function cotaDoEnvio(textos: string[]): Cota {
  const tamanhos = textos.map((t) => t.length);
  const total = tamanhos.reduce((s, n) => s + n, 0);
  const paginasTotais = textos.reduce((s, t) => s + contarPaginas(t), 0);

  if (textos.length <= 1) {
    const enviados = Math.min(total, TETO_DE_TEXTO);
    return {
      enviados,
      deFora: total - enviados,
      cotaPorArquivo: null,
      cabe: total <= TETO_DE_TEXTO,
      // As páginas do que CABE, contadas no recorte — nunca estimadas por
      // divisor. MEDIDO em 02/09/2026 no processo real que Victor entregou: a
      // média é 1.771 caracteres por página, e a estimativa com divisor de 2.400
      // dizia "cerca de 23 páginas ficam de fora" quando o número real era 33.
      paginasLidas: contarPaginas((textos[0] ?? "").slice(0, TETO_DE_TEXTO)),
      paginasTotais,
    };
  }

  const cota = Math.floor(TETO_DE_TEXTO / textos.length);
  // Cada arquivo contribui o MENOR entre o tamanho dele e a cota — um despacho
  // de duas páginas não usa 30 mil caracteres só porque tem direito a eles.
  const enviados = tamanhos.reduce((s, n) => s + Math.min(n, cota), 0);
  const paginasLidas = textos.reduce((s, t) => s + contarPaginas(t.slice(0, cota)), 0);
  return {
    enviados,
    deFora: total - enviados,
    cotaPorArquivo: cota,
    cabe: enviados === total,
    paginasLidas,
    paginasTotais,
  };
}

/** "1.234 caracteres" com o separador brasileiro. */
export function comMilhar(n: number): string {
  return n.toLocaleString("pt-BR");
}

/**
 * "1 página" ou "12 páginas" — a concordância num lugar só.
 *
 * A primeira versão da tela escrevia `cerca de {paginasAproximadas(n)} páginas`
 * e a captura mostrou **"cerca de 1 páginas ficaram de fora"**. Erro de
 * concordância em texto que fala de um número é o tipo de coisa que só a foto
 * pega: o `tsc` não vê, e nenhuma asserção de texto reclamaria.
 *
 * Recebe o NÚMERO DE PÁGINAS, não caracteres. A versão anterior recebia
 * caracteres e dividia por um divisor de 2.400 — que o processo real de Victor
 * derrubou em 02/09/2026: a média dele é 1.771, e a conta errava 30% (dizia 23
 * páginas de fora onde eram 33). O divisor saiu do módulo, e as páginas passaram
 * a ser CONTADAS nos marcadores `[Página N]` (000173).
 */
export function paginasEmTexto(paginas: number): string {
  const n = Math.max(0, Math.round(paginas));
  return `${comMilhar(n)} ${n === 1 ? "página" : "páginas"}`;
}

// ===========================================================================
// A BUSCA no relatório
// ===========================================================================

/**
 * O texto como a busca o compara: minúsculas e SEM ACENTO.
 *
 * Sem isto, procurar "cartoes" não acha "cartões" e procurar "pericia" não acha
 * "perícia" — e o perito digita sem acento quando está com pressa. É a mesma
 * razão de a lista de palavras-chave da detecção ter as duas grafias, e a
 * cicatriz do radical `contabil` que não casava "Contábeis".
 *
 * `NFD` separa a letra do acento; o intervalo `̀-ͯ` são as marcas
 * combinantes. Escrito com o escape unicode de propósito: colar os caracteres
 * combinantes literalmente num arquivo os torna invisíveis para quem lê o
 * código, e já foi assim que um sentinela virou byte nulo nesta casa.
 */
export function normalizarBusca(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** O termo aparece no texto? Comparação sem acento e sem caixa. */
export function acha(texto: string | null | undefined, termo: string): boolean {
  const t = termo.trim();
  if (!t) return true;
  if (!texto) return false;
  return normalizarBusca(texto).includes(normalizarBusca(t));
}

/**
 * Fatia o texto nos trechos que casam com o termo, para a tela pintá-los.
 *
 * Devolve pedaços `{texto, marcado}` em vez de HTML: é a mesma decisão de
 * `lib/textoRico.ts` — não existe `dangerouslySetInnerHTML` no hub, e aqui o
 * texto vem de um modelo alimentado com o processo de um terceiro.
 *
 * A comparação é feita sobre o texto NORMALIZADO, e os índices são usados para
 * cortar o texto ORIGINAL: assim "cartoes" pinta "cartões" com o acento no
 * lugar. A normalização preserva o comprimento (só remove marcas combinantes,
 * que não contam como caractere base), e o caso 13 da prova pura confere isso —
 * se um dia deixar de preservar, os índices deslocariam e a tela pintaria o
 * pedaço errado.
 */
export function fatiarPelaBusca(
  texto: string,
  termo: string,
): { texto: string; marcado: boolean }[] {
  const t = termo.trim();
  if (!t || !texto) return [{ texto, marcado: false }];

  const alvo = normalizarBusca(texto);
  const agulha = normalizarBusca(t);
  if (!agulha || alvo.length !== texto.length) return [{ texto, marcado: false }];

  const pedacos: { texto: string; marcado: boolean }[] = [];
  let de = 0;
  let i = alvo.indexOf(agulha);
  while (i >= 0) {
    if (i > de) pedacos.push({ texto: texto.slice(de, i), marcado: false });
    pedacos.push({ texto: texto.slice(i, i + agulha.length), marcado: true });
    de = i + agulha.length;
    i = alvo.indexOf(agulha, de);
  }
  if (de < texto.length) pedacos.push({ texto: texto.slice(de), marcado: false });
  return pedacos;
}

/**
 * O título da análise, a partir dos nomes dos arquivos.
 *
 * Um arquivo: o nome dele sem a extensão. Vários: o primeiro mais a contagem.
 * A identificação do processo que a IA extrai NÃO entra aqui — ela é derivada do
 * resultado na tela, e estampá-la no título criaria um segundo lugar dizendo
 * qual processo é este (a cicatriz da variante de contrato da 000051).
 */
export function tituloDoEnvio(nomes: string[]): string {
  const limpo = (n: string) => n.replace(/\.pdf$/i, "").trim() || "documento";
  if (nomes.length === 0) return "Processo sem arquivo";
  if (nomes.length === 1) return limpo(nomes[0]).slice(0, 200);
  return `${limpo(nomes[0])} + ${nomes.length - 1} arquivo${nomes.length > 2 ? "s" : ""}`.slice(0, 200);
}
